import type { ParseKeys } from "i18next";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FadeSlideSwitcher } from "../../../../common/components/fade-slide-switcher/FadeSlideSwitcher";
import { CloseXIcon } from "../../../../assets/icons";
import { RootState } from "../../../../stores/store";
import { SetPinContent } from "./content/setPinContent";
import { EnterPinContent } from "./content/enterPinContent";
import { ResetPinContent } from "./content/resetPinContent";
import { SuccessScreen } from "../success-screen";
import styles from "./CustomerPinModal.module.scss";

export type CustomerPinContent = "setPinContent" | "enterPinContent" | "resetPinContent";

export interface CustomerPinModalProps {
	onClose: (result?: string) => void;
	initialContent?: CustomerPinContent;
	onPinSubmitted?: (pin: string) => void;
	submitButtonText?: string;
	/** Show a success screen after a PIN reset instead of resolving straight away. */
	showResetSuccessScreen?: boolean;
}

const TITLE_KEYS: Record<CustomerPinContent, ParseKeys> = {
	setPinContent: "pin.setTransactionPin",
	enterPinContent: "pin.enterTransactionPin",
	resetPinContent: "pin.resetTransactionPin",
};

export const CustomerPinModal: React.FC<CustomerPinModalProps> = ({
	onClose,
	initialContent,
	onPinSubmitted,
	submitButtonText,
	showResetSuccessScreen = false,
}) => {
	const { t } = useTranslation();
	const hasPin = useSelector((state: RootState) => state.user.hasPin);

	const resolveInitialContent = (): CustomerPinContent => {
		if (initialContent && hasPin) return initialContent;
		return hasPin ? "enterPinContent" : "setPinContent";
	};

	const [currentContent, setCurrentContent] = useState<CustomerPinContent>(resolveInitialContent);
	const [resetSucceeded, setResetSucceeded] = useState(false);

	if (resetSucceeded) {
		return (
			<SuccessScreen
				title={t("buyElectricity.web.pinResetSuccessful")}
				subtitle={t("buyElectricity.web.useNewPinToFinishTransaction")}
				buttonText={t("common.actions.continueKey")}
				onContinue={() => onClose("pinReset")}
			/>
		);
	}

	return (
		<div className={styles.overlay} onClick={() => onClose()}>
			<div className={styles.sheet} onClick={e => e.stopPropagation()}>
				<div className={styles.header}>
					<button
						type="button"
						className={styles.closeBtn}
						onClick={() => onClose()}
						aria-label={t("account.web.close")}
					>
						<CloseXIcon />
					</button>
					<span className={styles.headerTitle}>{t(TITLE_KEYS[currentContent])}</span>
				</div>
				<hr className={styles.headerDivider} />

				<div className={styles.body}>
					<FadeSlideSwitcher switchKey={currentContent}>
						<div>
							{currentContent === "setPinContent" && (
								<SetPinContent
									onSetPinSubmitted={() => setCurrentContent("enterPinContent")}
								/>
							)}

							{currentContent === "enterPinContent" && (
								<EnterPinContent
									submitButtonText={submitButtonText}
									onPinSubmitted={pin => {
										onPinSubmitted?.(pin);
										onClose(pin);
									}}
									onForgotPinPressed={() => setCurrentContent("resetPinContent")}
								/>
							)}

							{currentContent === "resetPinContent" && (
								<ResetPinContent
									onPinReset={() => {
										if (showResetSuccessScreen) {
											setResetSucceeded(true);
										} else if (initialContent === "resetPinContent") {
											onClose("pinReset");
										} else if (initialContent) {
											setCurrentContent(initialContent);
										} else {
											setCurrentContent("enterPinContent");
										}
									}}
								/>
							)}
						</div>
					</FadeSlideSwitcher>
				</div>
			</div>
		</div>
	);
};

export default CustomerPinModal;
